import React, { useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import Chart from '../../common/Chart/Chart'
import ChartTooltip from '../../common/ChartTooltip/ChartTooltip'
import TDrawer from '../../common/TDrawer/TDrawer'

import {
  getADXSeries,
  getATRSeries,
  getBBHighSeries,
  getBBLowSeries,
  getBBRegressionOverlaySeries,
  getDIMinusSeries,
  getDIPlusSeries,
  getEMASeries,
  getHMASeries,
  getMACDHistogramSeries,
  getMACDSeries,
  getMACDSignalSeries,
  getMainChartSeries,
  getRSISeries,
  getSquizeMomentumSeries,
  getSquizeMomentumSeries2,
  getStochDSeries,
  getStochKSeries,
  getTrendiloDownSeries,
  getTrendiloRmsMinusSeries,
  getTrendiloRmsPlusSeries,
  getTrendiloUpSeries,
  getVolumeOSeries,
  getVWAPSeries,
  getWave1Series,
  getWave2Series,
  getWave3Series,
  getSupertrendSeries
} from '../../utils/strategies.util'

import './AccountChartsModal.scss'

const omittedSeriesProperties = ['time', 'color', 'trendiloRmsMinus', 'trendiloRmsPlus']

export const AccountChartsModal = ({
  account,
  indicatorsData,
  isOpen,
  klines,
  onResolve,
  title,
  centerTime
}) => {
  const chartsRef = useRef([])
  const tooltipRef = useRef()
  const mainChartSeries = useMemo(() => {
    return getMainChartSeries(klines, account, indicatorsData)
  }, [account, klines, indicatorsData])
  const bbLowSeries = useMemo(() => {
    return getBBLowSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const bbHighSeries = useMemo(() => {
    return getBBHighSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const bbRegressionSeries = useMemo(() => {
    return getBBRegressionOverlaySeries(klines, indicatorsData, account)
  }, [account, indicatorsData, klines])
  const emaSeries = useMemo(() => {
    return getEMASeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const hmaSeries = useMemo(() => {
    return getHMASeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const vwapSeries = useMemo(() => {
    return getVWAPSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const supertrendSeries = useMemo(() => {
    return getSupertrendSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const rsiSeries = useMemo(() => {
    return getRSISeries(klines, indicatorsData, account)
  }, [account, indicatorsData, klines])
  const stochKSeries = useMemo(() => {
    return getStochKSeries(klines, indicatorsData, account)
  }, [account, indicatorsData, klines])
  const stochDSeries = useMemo(() => {
    return getStochDSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const wave1Series = useMemo(() => {
    return getWave1Series(klines, indicatorsData, account)
  }, [account, indicatorsData, klines])
  const wave2Series = useMemo(() => {
    return getWave2Series(klines, indicatorsData, account)
  }, [account, indicatorsData, klines])
  const wave3Series = useMemo(() => {
    return getWave3Series(klines, indicatorsData, account)
  }, [account, indicatorsData, klines])
  const trendiloDownSeries = useMemo(() => {
    return getTrendiloDownSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const trendiloUpSeries = useMemo(() => {
    return getTrendiloUpSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const trendiloRmsPlusSeries = useMemo(() => {
    return getTrendiloRmsPlusSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const trendiloRmsMinusSeries = useMemo(() => {
    return getTrendiloRmsMinusSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const volumeOSeries = useMemo(() => {
    return getVolumeOSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const macdSeries = useMemo(() => {
    return getMACDSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const macdSignalSeries = useMemo(() => {
    return getMACDSignalSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const macdHistogramSeries = useMemo(() => {
    return getMACDHistogramSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const adxSeries = useMemo(() => {
    return getADXSeries(klines, indicatorsData, account)
  }, [account, indicatorsData, klines])
  const diPlusSeries = useMemo(() => {
    return getDIPlusSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const diMinusSeries = useMemo(() => {
    return getDIMinusSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const atrSeries = useMemo(() => {
    return getATRSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const squizeMomentumSeries = useMemo(() => {
    return getSquizeMomentumSeries(klines, indicatorsData)
  }, [indicatorsData, klines])
  const squizeMomentumSeries2 = useMemo(() => {
    return getSquizeMomentumSeries2(klines, indicatorsData)
  }, [indicatorsData, klines])

  const logicalRangeChange = (newLogicalRange) => {
    chartsRef.current.forEach(({ chart }) => {
      const currentLogicalRange = chart.timeScale().getVisibleLogicalRange()

      if (!isEqual(currentLogicalRange, newLogicalRange) && isOpen) {
        chart.timeScale().setVisibleLogicalRange(newLogicalRange)
      }
    })
  }

  const crosshairMove = (params => {
    if (
      params.point === undefined ||
      params.sourceEvent === undefined ||
      !params.time
    ) {
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none'
      }
    } else {
      const tooltipItems = []
      let tooltipContent = []

      chartsRef.current.forEach((chartData) => {
        const isHoveredChart = Array.from(params.seriesData.keys())[0] === chartData.chartSeries[0].series

        chartData.chart.setCrosshairPosition(
          isHoveredChart ? chartData.chartSeries[0].series.coordinateToPrice(params.point.y) : null,
          params.time,
          chartData.chartSeries[0].series
        )

        chartData.chartSeries.forEach(({ series, name, options }) => {
          const seriesActiveItem = series.dataByIndex(params.logical)

          if (!seriesActiveItem) {
            return
          }

          const optionStyle = {
            color: options?.color ?? 'inherit'
          }

          for (let prop in seriesActiveItem) {
            if (omittedSeriesProperties.includes(prop) || omittedSeriesProperties.includes(name)) continue

            if ('color' in seriesActiveItem) {
              optionStyle.color = seriesActiveItem.color
            }

            tooltipContent.push(
              <div style={optionStyle} key={prop === 'value' ? name : prop}>
                {prop === 'value' ? name : prop}: {seriesActiveItem[prop]}
              </div>)

            tooltipItems.push({
              color: optionStyle.color,
              key: prop === 'value' ? name : prop,
              label: prop === 'value' ? name : prop,
              value: seriesActiveItem[prop]
            })
          }
        })
      })

      if (tooltipRef.current) {
        tooltipRef.current.replaceChildren()

        tooltipItems.forEach(({ color, key, label, value }) => {
          const item = document.createElement('div')

          item.style.color = color
          item.dataset.key = key
          item.textContent = `${label}: ${value}`

          tooltipRef.current.appendChild(item)
        })

        tooltipRef.current.style.display = 'block'
        tooltipRef.current.style.position = 'fixed'
        tooltipRef.current.style.left = params.sourceEvent.clientX - 200 + 'px'
        tooltipRef.current.style.top = params.sourceEvent.clientY + 'px'
      }
    }
  })

  useEffect(() => {
    if (!centerTime) return

    const centerSec = Number(centerTime) / 1000

    let attempts = 0
    const maxAttempts = 20
    const attemptDelay = 50

    const tryCenter = () => {
      attempts += 1

      if (!chartsRef.current || chartsRef.current.length === 0) {
        if (attempts < maxAttempts) {
          setTimeout(tryCenter, attemptDelay)
        }
        return
      }

      const seriesData = mainChartSeries?.seriesData ?? []

      if (!seriesData.length) return

      let targetIndex = seriesData.findIndex(item => item.time >= centerSec)

      if (targetIndex === -1) targetIndex = seriesData.length - 1
      else if (targetIndex > 0) {
        const prev = seriesData[targetIndex - 1]
        const cur = seriesData[targetIndex]
        if (Math.abs(prev.time - centerSec) < Math.abs(cur.time - centerSec)) targetIndex -= 1
      }

      const lastIndex = seriesData.length - 1

      chartsRef.current.forEach(({ chart }) => {
        try {
          const visibleLogicalRange = chart.timeScale().getVisibleLogicalRange()
          const visibleFrom = visibleLogicalRange?.from ?? Math.max(0, lastIndex - 50)
          const visibleTo = visibleLogicalRange?.to ?? lastIndex
          const visibleBars = Math.max(1, Math.floor(visibleTo - visibleFrom))

          let from = Math.floor(targetIndex - visibleBars / 2)
          if (from < 0) from = 0
          let to = Math.floor(from + visibleBars)

          if (to > lastIndex) {
            to = lastIndex
            from = Math.max(0, to - visibleBars)
          }

          chart.timeScale().setVisibleLogicalRange({ from, to })
        } catch (err) {
          // ignore transient errors while chart initializes
        }
      })
    }

    tryCenter()
  }, [centerTime, mainChartSeries])

  return (
    <TDrawer
      isOpen={isOpen}
      onResolve={onResolve}
      title={title}>
      <div>
        <Chart series={[mainChartSeries, bbHighSeries, bbLowSeries, ...bbRegressionSeries, emaSeries, hmaSeries, vwapSeries, supertrendSeries].filter(Boolean)}
          chartHeightCoef={0.4}
          showNavigationControls
          ref={{ chartsRef }}
          logicalRangeChange={logicalRangeChange}
          crosshairMove={crosshairMove}>
        </Chart>
        {rsiSeries &&
          <Chart series={[rsiSeries]}
            name="RSI"
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {squizeMomentumSeries &&
          <Chart series={[squizeMomentumSeries, squizeMomentumSeries2]}
            chartHeightCoef={0.25}
            name="Squize Momentum"
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {trendiloDownSeries && trendiloUpSeries && trendiloRmsPlusSeries && trendiloRmsMinusSeries &&
          <Chart series={[trendiloUpSeries, trendiloDownSeries, trendiloRmsPlusSeries, trendiloRmsMinusSeries]}
            name="Trendilo"
            chartHeightCoef={0.25}
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {wave1Series && wave2Series && wave3Series &&
          <Chart series={[wave1Series, wave2Series, wave3Series]}
            name="Wave"
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {volumeOSeries &&
          <Chart series={[volumeOSeries]}
            name="Volume O"
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {macdSeries && macdSignalSeries && macdHistogramSeries &&
          <Chart series={[macdHistogramSeries, macdSeries, macdSignalSeries]}
            name="MACD"
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {adxSeries &&
          <Chart series={[adxSeries, diPlusSeries, diMinusSeries].filter(Boolean)}
            name={diPlusSeries && diMinusSeries ? 'ADX + DI' : 'ADX'}
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {atrSeries &&
          <Chart series={[atrSeries]}
            name="ATR"
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
        {stochKSeries && stochDSeries &&
          <Chart series={[stochKSeries, stochDSeries]}
            name="Stoch"
            ref={{ chartsRef }}
            logicalRangeChange={logicalRangeChange}
            crosshairMove={crosshairMove}>
          </Chart>}
      </div>
      <ChartTooltip ref={tooltipRef} />
    </TDrawer>
  )
}
AccountChartsModal.displayName = 'AccountChartsModal'
AccountChartsModal.propTypes = {
  account: PropTypes.shape({}).isRequired,
  indicatorsData: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
  klines: PropTypes.shape({}).isRequired,
  onResolve: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired
}

export default AccountChartsModal
